// JAWS vs VoiceOver (see doc/jaws-voiceover-accessibility-reference.md §5):
// Question progress is an sr-only h2 (aria-describedby on the question h1).
// Visible progress dots are decorative (aria-hidden). aria-pressed on the voice button
// covers recording state; #aria_assertive is used only for microphone permission errors.

import React, { useEffect, useRef, useState } from 'react'
import YnsIcon from '../icons/YnsIcon'
import { Box } from '../_atoms/index'
import useTranslation from '../shared/TranslationProvider'
import { Button } from '~/lib/ui/button'
import { Progress } from '~/lib/ui/progress'
import { cn } from '~/lib/utils'
import { SpinnerWhite } from '../shared/Spinner'
import { Textarea } from '~/lib/ui/textarea'
import { useSelectOnboarding } from './SelectOnboardingProvider'
import { ToggleGroup, ToggleGroupItem } from '~/lib/ui/toggle-group'
import { useAnnouncer } from '../OnboardingSteps/Announcer'
import StepProgressHeading from '../OnboardingSteps/StepProgressHeading'
import { stepDescribedBy } from '../OnboardingSteps/stepProgress'

const FormPage = () => {
  const { t } = useTranslation()
  const { questionIdx, questions } = useSelectOnboarding()
  const [values, setValues] = useState({})

  return (
    <div className="bg-select-onboarding-gradient pb-8">
      <Progress
        aria-hidden="true"
        className="h-2 rounded-none bg-transparent"
        indicatorClassName="bg-primary rounded-none"
        value={(questionIdx / questions.length) * 100}
      />
      <Box className="mx-4 mt-8 flex max-w-3xl flex-col flex-wrap content-center rounded-[20px] pt-10 md:mx-auto">
        <Box className="flex justify-between">
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-primary ">
              <YnsIcon
                name="onboarding"
                className="text-white"
                aria-hidden="true"
              />
            </div>
            <span>{t('SelectOnboarding.page_title')}</span>
          </div>
          <div className="flex gap-2" aria-hidden="true">
            {questions.map((_, idx) => (
              <ProgressDot
                key={idx}
                active={idx + 1 === questionIdx}
                completed={idx + 1 < questionIdx}
              />
            ))}
          </div>
        </Box>
        {questions.map(
          (_q, idx) =>
            idx + 1 === questionIdx && (
              <FormQuestion
                key={_q.name}
                values={values}
                setValues={setValues}
              />
            ),
        )}
      </Box>
    </div>
  )
}

const StartIcon = () => (
  <YnsIcon
    name="microphone"
    className="inline h-5 w-5 text-white"
    aria-hidden="true"
  />
)
const RecordingIcon = () => (
  <YnsIcon
    name="stop"
    className="inline h-5 w-5 text-red-800"
    aria-hidden="true"
  />
)
const FormQuestion = ({ values, setValues }) => {
  const {
    questions,
    questionIdx,
    setQuestionIdx,
    updateOnboardingMutation,
    onLastQuestion,
  } = useSelectOnboarding()
  const announce = useAnnouncer()
  const [isLoading, setIsLoading] = useState(false)
  const [supportNeeded, setSupportNeeded] = useState(
    values['support_needed'] ? values['support_needed'] : [],
  )
  const questionInfo = questions[questionIdx - 1]
  const { name, label, choices, placeholder } = questionInfo
  const questionHeadingId = `select-onboarding-question-${name}`
  const suggestionsId = `${questionHeadingId}-suggestions`
  const [recording, setRecording] = useState(false)
  const recognitionRef = useRef(null)
  const committedTranscriptRef = useRef('')
  const lastFinalTranscriptRef = useRef('')
  const { t } = useTranslation('SelectOnboarding')
  const buttonText = onLastQuestion ? t('submit') : t('continue')

  const handleSubmit = () => {
    setIsLoading(true)
    if (supportNeeded.length > 0) {
      setValues((prev) => ({ ...prev, support_needed: supportNeeded }))
    }
    const newValues = { ...values, support_needed: supportNeeded }
    updateOnboardingMutation.mutate(newValues)
  }

  const handleBackClick = () => {
    if (recording) {
      recognitionRef.current.stop()
      setRecording(false)
    } else setQuestionIdx((idx) => idx - 1)
  }

  useEffect(() => {
    if (!recognitionRef.current) {
      const RecognitionClass =
        window.SpeechRecognition ||
        window.webkitSpeechRecognition ||
        window.mozSpeechRecognition ||
        window.msSpeechRecognition
      recognitionRef.current = new RecognitionClass()
      recognitionRef.current.lang = 'en-US'
      recognitionRef.current.continuous = true
      recognitionRef.current.interimResults = true
    }
    const recognition = recognitionRef.current

    recognition.onresult = (e) => {
      let interimTranscript = ''
      let finalTranscript = ''
      for (let i = 0; i < e.results.length; ++i) {
        const result = e.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          interimTranscript += result[0].transcript
        }
      }
      if (finalTranscript) {
        const prevFinal = lastFinalTranscriptRef.current
        let newPart = finalTranscript
        if (finalTranscript.startsWith(prevFinal)) {
          newPart = finalTranscript.slice(prevFinal.length)
        }
        committedTranscriptRef.current = (
          committedTranscriptRef.current + (newPart ? ' ' + newPart : '')
        ).trim()
        lastFinalTranscriptRef.current = finalTranscript
      }
      setValues((prev) => ({
        ...prev,
        [name]: (
          committedTranscriptRef.current +
          (interimTranscript ? ' ' + interimTranscript : '')
        ).trim(),
      }))
    }
    recognition.onspeechend = () => {
      recognition.stop()
      setRecording(false)
    }
    recognition.onerror = (e) => {
      console.error('Speech recognition error', e)
      if (e.error === 'not-allowed') {
        announce(t('voice_to_text_permission_denied'), { assertive: true })
      }
      setRecording(false)
    }
    return () => {
      recognition.stop()
    }
  }, [announce, name, setValues, t])

  const handleStartRecognition = () => {
    committedTranscriptRef.current = values[name] || ''
    lastFinalTranscriptRef.current = ''
    recognitionRef.current.start()
    setRecording(true)
  }

  const handleStopRecording = () => {
    recognitionRef.current.stop()
    setRecording(false)
  }

  const Icon = recording ? RecordingIcon : StartIcon

  const handleButtonClick = () => {
    if (recording) handleStopRecording()
    else handleStartRecognition()
  }

  const handleValueChange = (value) => {
    setSupportNeeded(value)
  }

  return (
    <Box className="flex w-full flex-col items-center gap-4 pt-2">
      <Box className="flex w-full flex-col gap-2 p-0 md:p-6">
        <h1
          id={questionHeadingId}
          data-onboarding-step-heading
          tabIndex={-1}
          aria-describedby={stepDescribedBy()}
          className="subtitle mb-2 leading-[1.3] focus:outline-none"
        >
          {label}
        </h1>
        <StepProgressHeading>
          {t('progress_text', {
            number: questionIdx,
            total: questions.length,
          })}
        </StepProgressHeading>
        {choices && (
          <>
            <div id={suggestionsId}>{t('suggestions')}</div>
            <ToggleGroup
              type="multiple"
              onValueChange={handleValueChange}
              aria-labelledby={`${questionHeadingId} ${suggestionsId}`}
            >
              <div className="my-2 grid grid-cols-1 gap-2 md:grid-cols-2">
                {choices.map((choice) => (
                  <ToggleGroupItem
                    key={choice.name}
                    value={choice.name}
                    className="flex flex-col items-start rounded-md border border-intoo-blue-medium bg-white p-2 text-intoo-blue-medium hover:bg-intoo-blue-light data-[state=on]:bg-intoo-blue-medium data-[state=on]:text-white data-[state=on]:hover:bg-intoo-blue-medium"
                  >
                    {choice.label}
                  </ToggleGroupItem>
                ))}
              </div>
            </ToggleGroup>
          </>
        )}
        <Textarea
          aria-labelledby={questionHeadingId}
          placeholder={recording ? '...' : placeholder}
          className={cn('min-h-[120px] resize-none md:min-h-[250px]')}
          value={values[name] || ''}
          onChange={(e) => {
            committedTranscriptRef.current = e.target.value
            lastFinalTranscriptRef.current = ''
            setValues((prev) => ({ ...prev, [name]: e.target.value }))
          }}
        />
        <Button
          onClick={handleButtonClick}
          type="button"
          className="h-12 w-12 self-end rounded-full p-0"
          aria-label={t('voice_to_text')}
          aria-pressed={recording}
        >
          <Icon />
        </Button>
      </Box>
      {onLastQuestion && (
        <Box className="pt-0">
          <p>{t('thank_you')}</p>
          <p>{t('conclusion')}</p>
        </Box>
      )}
      <Box className="flex w-full justify-between gap-2 md:gap-0">
        <Button
          variant="outline"
          disabled={questionIdx === 0}
          type="button"
          onClick={handleBackClick}
        >
          {t('back')}
        </Button>
        <div
          className="flex flex-col items-center gap-2 pt-4 text-gray-600"
          aria-hidden="true"
        >
          {t('progress_text', {
            number: questionIdx,
            total: questions.length,
          })}
        </div>
        <Button className="flex items-center gap-2" onClick={handleSubmit}>
          {buttonText}
          <SpinnerWhite show={isLoading} />
        </Button>
      </Box>
    </Box>
  )
}

const ProgressDot = ({ active, completed }) => {
  const baseClasses = 'h-2 rounded-full border-2'
  const activeClasses = 'bg-primary border-primary w-4'
  const completedClasses = 'bg-intoo-green-bright border-intoo-green-bright w-2'
  const inactiveClasses = 'bg-[#D1D5DC] border-[#D1D5DC] w-2'

  const classes = active
    ? activeClasses
    : completed
      ? completedClasses
      : inactiveClasses

  return <div className={cn(baseClasses, classes)}></div>
}

export default FormPage
